import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export function useSourceStatus(path) {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(() => api.get(path).then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false })), [path]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (status?.sync_status !== "running") return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [status?.sync_status, refresh]);
  return [status, setStatus, refresh];
}

export const SourceShell = ({ icon: Icon, title, subtitle, badge, children, testid }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-8 animate-rise" data-testid={testid}>
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-amber-50 text-amber-700"><Icon size={20} /></div>
        <div>
          <h2 className="text-lg font-medium">{title}</h2>
          <p className="text-xs text-zinc-500">{subtitle}</p>
        </div>
      </div>
      {badge}
    </div>
    {children}
  </section>
);

export const StatusPill = ({ status, testid }) => {
  const s = status?.sync_status;
  const map = { running: ["bg-amber-100 text-amber-800", "Scanning"], error: ["bg-red-50 text-red-700", "Error"], done: ["bg-emerald-50 text-emerald-700", "Up to date"] };
  const [cls, label] = status?.connected ? map[s] || ["bg-zinc-100 text-zinc-600", "Connected"] : ["bg-zinc-100 text-zinc-500", "Not connected"];
  return <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${cls}`} data-testid={testid}>{label}</span>;
};

export const SyncLine = ({ status, testid }) => {
  if (!status?.connected) return null;
  const s = status.last_sync_summary;
  return (
    <div className="mt-6 rounded-md bg-[#FAF9F6] px-4 py-3 font-mono text-xs text-zinc-600" data-testid={testid}>
      {status.sync_status === "running" && <span>Scanning… {status.found_files ?? status.found_items ?? 0} videos found so far</span>}
      {status.sync_status === "error" && <span className="text-red-700">{status.sync_error}</span>}
      {status.sync_status !== "running" && status.sync_status !== "error" && (
        <span>{status.videos} videos in library{s ? ` · last scan +${s.added} new, −${s.removed} removed · ${new Date(s.finished_at).toLocaleString()}` : ""}</span>
      )}
    </div>
  );
};

export const PillButton = ({ children, onClick, disabled, variant = "dark", testid, type = "button" }) => (
  <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex h-10 items-center gap-2 rounded-full px-5 text-sm transition-[background-color,border-color,transform] active:scale-[0.98] disabled:opacity-50 ${
      variant === "dark" ? "bg-zinc-900 text-white hover:bg-zinc-800" : variant === "danger" ? "border border-red-200 text-red-700 hover:border-red-500" : "border border-zinc-300 bg-white hover:border-zinc-900"}`}>
    {children}
  </button>
);
